import React, { useState } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useContent } from "../contexts/ContentContext";

export default function Testimonials() {
  const { content } = useContent();
  const { testimonials } = content;
  const [idx, setIdx] = useState(0);
  const total = testimonials.length;
  const t = testimonials[idx];

  return (
    <section className="py-20 lg:py-28 bg-gradient-to-b from-emerald-50/40 to-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-block px-3 py-1 rounded-full bg-orange-50 text-orange-600 text-xs font-semibold tracking-wider mb-4">
            CUSTOMER STORIES
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900">
            What Our <span className="text-orange-500">Customers</span> Say
          </h2>
          <p className="text-slate-600 mt-4">
            Real homeowners. Real savings. Real stories.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-3 gap-6">
          {/* Big featured card */}
          <div className="lg:col-span-2 relative bg-white rounded-3xl p-8 lg:p-10 shadow-lg border border-slate-200">
            <Quote className="absolute top-6 right-6 w-14 h-14 text-orange-100" />
            <div className="flex items-center gap-1 mb-5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={`w-5 h-5 ${
                    i < t.rating ? "fill-orange-400 text-orange-400" : "text-slate-300"
                  }`}
                />
              ))}
            </div>
            <p className="text-slate-700 text-lg md:text-xl leading-relaxed font-medium">
              &ldquo;{t.text}&rdquo;
            </p>
            <div className="mt-8 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img
                  src={t.image}
                  alt={t.name}
                  className="w-14 h-14 rounded-full object-cover ring-2 ring-orange-200"
                />
                <div>
                  <div className="font-bold text-slate-900">{t.name}</div>
                  <div className="text-sm text-slate-600">{t.location}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIdx((i) => (i - 1 + total) % total)}
                  className="w-10 h-10 rounded-full border border-slate-300 hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-colors flex items-center justify-center"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setIdx((i) => (i + 1) % total)}
                  className="w-10 h-10 rounded-full border border-slate-300 hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-colors flex items-center justify-center"
                  aria-label="Next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Side stack */}
          <div className="space-y-5">
            {testimonials.map((rev, i) => (
              <button
                key={`${rev.name}-${rev.location}-${i}`}
                onClick={() => setIdx(i)}
                className={`w-full text-left rounded-2xl p-5 border transition-all ${
                  i === idx
                    ? "bg-[#0a4f47] border-[#0a4f47] text-white shadow-lg"
                    : "bg-white border-slate-200 hover:border-orange-300 text-slate-900"
                }`}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={rev.image}
                    alt={rev.name}
                    className="w-11 h-11 rounded-full object-cover"
                  />
                  <div className="min-w-0">
                    <div className="font-bold truncate">{rev.name}</div>
                    <div className={`text-xs ${i === idx ? "text-white/80" : "text-slate-500"}`}>
                      {rev.location}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 mt-2">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star
                      key={k}
                      className={`w-3.5 h-3.5 ${
                        k < rev.rating
                          ? "fill-orange-400 text-orange-400"
                          : i === idx
                          ? "text-white/40"
                          : "text-slate-300"
                      }`}
                    />
                  ))}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
